import { useTranslation } from 'react-i18next';
import { Alert, Skeleton, Tag, Tooltip } from 'antd';
import {
  ArrowRightOutlined,
  CalendarOutlined,
  HistoryOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { cn } from '@/libs/utils';
import { formatDateTimeWithLocale, getUserFullName } from '@/utils/utils';
import {
  isCareerTimelineForbidden,
  useEmployeeCareerTimeline,
} from './useEmployeeCareerTimeline';

const KIND_META = {
  CONVERTED_TO_UNLIMITED: { color: 'green', labelKey: 'careerTimeline.convertedToUnlimited' },
  CONVERTED_TO_LIMITED: { color: 'orange', labelKey: 'careerTimeline.convertedToLimited' },
  EXTENDED: { color: 'blue', labelKey: 'careerTimeline.extended' },
  SHORTENED: { color: 'gold', labelKey: 'careerTimeline.shortened' },
  CONTRACT_OPENED: { color: 'green', labelKey: 'careerTimeline.contractOpened' },
  CONTRACT_CLOSED: { color: 'red', labelKey: 'careerTimeline.contractClosed' },
  START_DATE_CHANGED: { color: 'purple', labelKey: 'careerTimeline.startDateChanged' },
  WORK_TIME_CHANGED: { color: 'cyan', labelKey: 'careerTimeline.workTimeChanged' },
};

const fmtDate = (d) => (d ? formatDateTimeWithLocale(d, undefined, false) : '~');

function EventDetail({ event, t }) {
  const employmentTypeLabel = (key) => (key ? t(key, { ns: 'forms' }) : '~');

  switch (event.kind) {
    case 'CONVERTED_TO_UNLIMITED':
    case 'CONVERTED_TO_LIMITED': {
      const hasEndDateInfo = event.oldEndDate || event.newEndDate;
      return (
        <div className="grid gap-1 text-paragraph">
          <span className="inline-flex items-center gap-1">
            <span>{employmentTypeLabel(event.oldType)}</span>
            <ArrowRightOutlined className="text-xs opacity-60" />
            <span>{employmentTypeLabel(event.newType)}</span>
          </span>
          {hasEndDateInfo && (
            <span className="inline-flex items-center gap-1 text-xs opacity-70">
              <CalendarOutlined className="text-[10px]" />
              <span>{t('careerTimeline.endDateLabel', { ns: 'forms' })}:</span>
              <span>{fmtDate(event.oldEndDate)}</span>
              <ArrowRightOutlined className="text-[10px] opacity-60" />
              <span>{fmtDate(event.newEndDate)}</span>
            </span>
          )}
        </div>
      );
    }
    case 'EXTENDED':
    case 'SHORTENED':
    case 'CONTRACT_OPENED':
    case 'CONTRACT_CLOSED':
      return (
        <span className="inline-flex items-center gap-1 text-paragraph">
          <CalendarOutlined className="text-xs opacity-60" />
          <span>{t('careerTimeline.endDateLabel', { ns: 'forms' })}:</span>
          <span>{fmtDate(event.oldEndDate)}</span>
          <ArrowRightOutlined className="text-xs opacity-60" />
          <span>{fmtDate(event.newEndDate)}</span>
        </span>
      );
    case 'START_DATE_CHANGED':
      return (
        <span className="inline-flex items-center gap-1 text-paragraph">
          <CalendarOutlined className="text-xs opacity-60" />
          <span>{t('startDateOfWork', { ns: 'forms' })}:</span>
          <span>{fmtDate(event.oldStartDate)}</span>
          <ArrowRightOutlined className="text-xs opacity-60" />
          <span>{fmtDate(event.newStartDate)}</span>
        </span>
      );
    case 'WORK_TIME_CHANGED':
      return (
        <span className="inline-flex items-center gap-1 text-paragraph">
          <span>{t(event.oldWorkTime || '', { ns: 'forms' })}</span>
          <ArrowRightOutlined className="text-xs opacity-60" />
          <span>{t(event.newWorkTime || '', { ns: 'forms' })}</span>
        </span>
      );
    default:
      return null;
  }
}

export default function EmployeeCareerTimeline({
  employeeId,
  className,
  compact = false,
}) {
  const { t } = useTranslation(['forms', 'messages']);

  const { data, isLoading, error } = useEmployeeCareerTimeline(employeeId);

  const events = data?.timeline || [];

  if (isLoading) {
    return (
      <div className={cn('grid gap-2', className)}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  // The whole section is hidden by the parent on a 403; render nothing here too.
  if (isCareerTimelineForbidden(error)) {
    return null;
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={t('error', { ns: 'messages' })}
        description={
          error.response?.data?.message ||
          t('careerTimeline.errorLoading', { ns: 'forms' })
        }
        className={className}
      />
    );
  }

  if (!events.length) {
    return (
      <Alert
        type="info"
        showIcon
        message={t('careerTimeline.empty', { ns: 'forms' })}
        className={className}
      />
    );
  }

  return (
    <ul
      className={cn(
        'm-0 grid list-none gap-3 p-0',
        compact ? 'gap-2' : '',
        className
      )}
    >
      {events.map((event, idx) => {
        const meta = KIND_META[event.kind] || { color: 'default' };
        const label = meta.labelKey
          ? t(meta.labelKey, { ns: 'forms' })
          : event.kind;
        const userName = event.changedBy
          ? getUserFullName(event.changedBy.firstName, event.changedBy.lastName)
          : null;

        const recordedAtText = formatDateTimeWithLocale(event.timestamp);
        const effectiveDateText = event.effectiveDate
          ? formatDateTimeWithLocale(event.effectiveDate, undefined, false)
          : null;

        return (
          <li
            key={`${event.timestamp}-${event.kind}-${idx}`}
            className="grid gap-1 border-l-2 border-zinc-200 pl-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Tag color={meta.color} className="m-0">
                {label}
              </Tag>
              {effectiveDateText && (
                <Tooltip
                  title={t('careerTimeline.effectiveDateTooltip', {
                    ns: 'forms',
                  })}
                >
                  <span className="inline-flex items-center gap-1 rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-medium text-zinc-700">
                    <CalendarOutlined className="text-[10px]" />
                    {t('careerTimeline.effectiveFrom', { ns: 'forms' })}:{' '}
                    {effectiveDateText}
                  </span>
                </Tooltip>
              )}
              <Tooltip
                title={t('careerTimeline.recordedAtTooltip', { ns: 'forms' })}
              >
                <span className="text-xs text-zinc-500">
                  {t('careerTimeline.recordedAt', { ns: 'forms' })}:{' '}
                  {recordedAtText}
                </span>
              </Tooltip>
              {userName && (
                <Tooltip title={event.changedBy?.email || ''}>
                  <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                    <UserOutlined className="text-[10px]" />
                    {userName}
                  </span>
                </Tooltip>
              )}
            </div>
            <EventDetail event={event} t={t} />
          </li>
        );
      })}
    </ul>
  );
}

export function EmployeeCareerTimelineHeader({ className }) {
  const { t } = useTranslation(['forms', 'messages']);
  return (
    <h3
      className={cn(
        'text-[18px] font-normal text-paragraph',
        className
      )}
    >
      <HistoryOutlined className="mr-1" />
      <span>{t('careerTimeline.title', { ns: 'forms' })}</span>
    </h3>
  );
}
